import type { Json } from '../../supabase/types/app.database.types';

const DEFAULT_MAX_LENGTH = 64;
const SUFFIX_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

/**
 * Perform a deterministic JSON stringification by sorting keys recursively.
 */
export function stableStringify(value: unknown, seen: WeakSet<object> = new WeakSet()): string {
  if (value === null || typeof value === 'undefined') return 'null';
  if (typeof value === 'number' || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'bigint') return JSON.stringify(value.toString());
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  if (value instanceof RegExp) return JSON.stringify(value.toString());

  if (Array.isArray(value)) {
    const items = value.map((item) => stableStringify(item, seen));
    return `[${items.join(',')}]`;
  }

  if (typeof value === 'object') {
    if (seen.has(value as object)) {
      return '"[Circular]"';
    }
    seen.add(value as object);
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0))
      .map(([key, val]) => `${JSON.stringify(key)}:${stableStringify(val, seen)}`);
    return `{${entries.join(',')}}`;
  }

  return JSON.stringify(String(value));
}

/**
 * Sanitize an input string into a slug suitable for URLs.
 */
export function sanitizeSlug(input: string, options?: {
  maxLength?: number;
  fallback?: string;
}): string {
  const maxLength = options?.maxLength ?? DEFAULT_MAX_LENGTH;
  const fallback = options?.fallback ?? 'snapurl';
  const raw = (input ?? '').toString().trim().toLowerCase();

  const cleaned = raw
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  const truncated = cleaned.slice(0, maxLength).replace(/^-+|-+$/g, '');
  if (truncated) {
    return truncated;
  }

  const fallbackNormalized = (fallback ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength);

  return fallbackNormalized || 'snapurl';
}

async function sha256Hex(value: string): Promise<string> {
  if (typeof globalThis.crypto?.subtle?.digest !== 'function') {
    throw new Error('SHA-256 is not available in this environment');
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generate an 8-character slug hash derived from a template id and payload.
 */
export async function generateSlugHash(
  templateId: string,
  data: Json | Record<string, unknown> | undefined
): Promise<string> {
  const payload = `${templateId ?? ''}|${stableStringify(data ?? {})}`;
  const digest = await sha256Hex(payload);
  return digest.slice(0, 8);
}

function randomSuffix(length = 4): string {
  const bytes = new Uint8Array(length);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let suffix = '';
  for (let i = 0; i < length; i += 1) {
    suffix += SUFFIX_ALPHABET[bytes[i] % SUFFIX_ALPHABET.length];
  }
  return suffix;
}

/**
 * Generate a short random slug of specified length.
 */
export function generateShortSlug(length = 6): string {
  return randomSuffix(length);
}

export type SlugCollisionChecker = (candidate: string) => Promise<boolean> | boolean;

/**
 * Resolve slug collisions by appending a suffix until an unused slug is found.
 */
export async function resolveSlugCollision(
  baseSlug: string,
  isTaken: SlugCollisionChecker,
  options?: {
    maxAttempts?: number;
    suffixLength?: number;
    fallback?: string;
    maxLength?: number;
    suffixFactory?: (attempt: number) => string;
  }
): Promise<string> {
  if (typeof isTaken !== 'function') {
    throw new Error('resolveSlugCollision requires a predicate function');
  }

  const maxAttempts = options?.maxAttempts ?? 10;
  const suffixLength = options?.suffixLength ?? 4;
  const maxLength = options?.maxLength ?? DEFAULT_MAX_LENGTH;
  const fallback = options?.fallback;

  const base = sanitizeSlug(baseSlug, { maxLength, fallback });
  let exists: boolean;
  try {
    exists = await isTaken(base);
  } catch (error) {
    throw new Error(`Failed to check slug availability: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (!exists) {
    return base;
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const suffix = options?.suffixFactory?.(attempt) ?? randomSuffix(suffixLength);
    let cleanedSuffix = suffix.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, suffixLength);
    if (!cleanedSuffix) {
      cleanedSuffix = randomSuffix(Math.max(2, Math.min(4, suffixLength)));
    }

    const availableLength = Math.max(maxLength - cleanedSuffix.length - 1, 1);
    const truncatedBase = base.slice(0, availableLength).replace(/-+$/g, '');
    const candidate = `${truncatedBase}-${cleanedSuffix}`;

    try {
      exists = await isTaken(candidate);
    } catch (error) {
      throw new Error(`Failed to check slug availability: ${error instanceof Error ? error.message : String(error)}`);
    }

    if (!exists) {
      return candidate;
    }
  }

  throw new Error(`Unable to resolve slug collision after ${maxAttempts} attempts for base slug "${base}"`);
}

export default {
  sanitizeSlug,
  generateSlugHash,
  generateShortSlug,
  resolveSlugCollision,
  stableStringify,
};
